<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useBranding } from 'shared/composables/useBranding';

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  actionUrl: {
    type: String,
    default: '',
  },
  actionRoute: {
    type: Object,
    default: null,
  },
});

const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const normalizedStatus = computed(() => {
  if (props.status) return props.status;
  return props.enabled ? 'active' : 'disabled';
});

const statusCopy = computed(() => {
  const map = {
    active: {
      label: t('INTEGRATION_APPS.STATUS.ENABLED'),
      classes: 'border-n-teal-7 bg-n-teal-3/20 text-n-teal-10',
      dot: 'bg-n-teal-9',
    },
    disabled: {
      label: t('INTEGRATION_APPS.STATUS.DISABLED'),
      classes: 'border-n-weak bg-n-alpha-1 text-n-slate-9',
      dot: 'bg-n-slate-7',
    },
    pending: {
      label: 'Pendente',
      classes: 'border-n-slate-7 bg-n-slate-3/50 text-n-slate-11',
      dot: 'bg-n-slate-8',
    },
    error: {
      label: 'Com erro',
      classes: 'border-n-ruby-8/50 bg-n-ruby-4/20 text-n-ruby-10',
      dot: 'bg-n-ruby-9',
    },
  };

  return map[normalizedStatus.value] || map.disabled;
});

const actionTo = computed(() => {
  if (props.actionRoute) return props.actionRoute;

  return frontendURL(
    props.actionUrl ||
      `accounts/${accountId.value}/settings/integrations/${props.id}`
  );
});

const fallbackIcons = {
  banking: 'i-lucide-landmark',
  corban: 'i-lucide-handshake',
  erp: 'i-lucide-database',
  google_calendar: 'i-lucide-calendar-days',
  email_inbox: 'i-lucide-mail-plus',
  webhook: 'i-lucide-webhook',
  openai: 'i-lucide-sparkles',
  slack: 'i-lucide-message-square',
  shopify: 'i-lucide-shopping-bag',
  notion: 'i-lucide-file-text',
  linear: 'i-lucide-git-branch',
};

const fallbackIcon = computed(() => fallbackIcons[props.id]);
const actionLabel = computed(() =>
  normalizedStatus.value === 'active' ? 'Gerenciar' : 'Configurar'
);

const categories = computed(() => {
  const map = {
    banking: ['Bancos', 'Produtos'],
    corban: ['Corban', 'Consultas'],
    erp: ['ERP', 'Financeiro'],
    google_calendar: ['Agenda', 'Produtividade'],
    email_inbox: ['Email', 'Atendimento'],
    webhook: ['API', 'Eventos'],
    openai: ['IA', 'Atendimento'],
    slack: ['Equipe', 'Mensagens'],
    shopify: ['Comercio', 'Pedidos'],
    notion: ['Docs', 'Workspace'],
    linear: ['Projetos', 'Issues'],
  };

  return map[props.id] || ['Integração'];
});
</script>

<template>
  <article
    class="group relative flex min-h-[220px] flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-n-brand/50 hover:shadow-lg"
  >
    <div
      class="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-n-blue-8/70 to-transparent opacity-0 transition group-hover:opacity-100"
    />

    <div class="flex flex-1 flex-col p-4">
      <div class="mb-4 flex items-start justify-between gap-3">
        <div
          v-if="fallbackIcon"
          class="flex h-10 w-10 items-center justify-center rounded-lg border border-n-brand/20 bg-n-brand/10 text-n-brand shadow-sm transition group-hover:border-n-brand/50 group-hover:bg-n-brand/15"
        >
          <span :class="fallbackIcon" class="size-4" />
        </div>
        <div
          v-else
          class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 p-1.5"
        >
          <img
            :src="`/dashboard/images/integrations/${id}.png`"
            class="max-w-full rounded-lg block dark:hidden"
            alt=""
          />
          <img
            :src="`/dashboard/images/integrations/${id}-dark.png`"
            class="max-w-full rounded-lg hidden dark:block"
            alt=""
          />
        </div>

        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold"
            :class="statusCopy.classes"
          >
            <span class="h-1.5 w-1.5 rounded-full" :class="statusCopy.dot" />
            {{ statusCopy.label }}
          </span>
        </div>
      </div>

      <div class="flex flex-1 flex-col">
        <h3 class="text-sm font-semibold leading-snug text-n-slate-12">
          {{ name }}
        </h3>
        <p class="mt-2 line-clamp-2 text-xs leading-5 text-n-slate-10">
          {{ replaceInstallationName(description) }}
        </p>

        <div class="mt-3 flex flex-wrap gap-1.5">
          <span
            v-for="category in categories"
            :key="category"
            class="rounded-md border border-n-weak bg-n-alpha-2 px-2 py-0.5 text-[10px] font-semibold text-n-slate-10"
          >
            {{ category }}
          </span>
        </div>
      </div>
    </div>

    <div
      class="flex items-center justify-between gap-3 border-t border-n-weak bg-n-solid-2 px-4 py-3"
    >
      <span class="inline-flex items-center gap-1.5 text-[11px] text-n-slate-9">
        <span class="i-lucide-shield-check size-3.5" />
        Configuração protegida
      </span>
      <router-link
        :to="actionTo"
        class="inline-flex h-9 min-w-[104px] items-center justify-center gap-2 rounded-lg bg-n-brand px-3 text-xs font-semibold text-white shadow-sm transition hover:brightness-110"
      >
        <span class="i-woot-settings size-4 flex-shrink-0" />
        {{ actionLabel }}
        <span class="i-lucide-arrow-right size-4" />
      </router-link>
    </div>
  </article>
</template>
